import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDarkMode } from '../../../context/DarkModeContext';
import { useReviewChecklist, ChecklistItem } from '../../../hooks/useReviewChecklist';
import { 
  CheckCircleIcon, 
  ClockIcon,
  DocumentTextIcon,
  ChatBubbleLeftRightIcon,
  UserGroupIcon,
  DocumentDuplicateIcon,
  SparklesIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleIconSolid } from '@heroicons/react/24/solid';
import { supabase } from '../../../utils/supabase';
import { useAuthState } from '../../../hooks/useAuthState';
import { devLog } from '../../../utils/devLogger';

interface FinalWeekChecklistProps {
  focusId: string;
  daysUntilReview: number;
  hasCompletedPrep: boolean;
  evidenceCoverage: number;
}

const FinalWeekChecklist: React.FC<FinalWeekChecklistProps> = ({ 
  focusId, 
  daysUntilReview,
  hasCompletedPrep,
  evidenceCoverage
}) => {
  const { darkMode } = useDarkMode();
  const { user } = useAuthState();
  const [checkedItems, setCheckedItems] = useState<Set<string>>(new Set());
  const [isExpanded, setIsExpanded] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const { checklist: dynamicChecklist, loading: checklistLoading } = useReviewChecklist(focusId);
  
  const bgColor = darkMode ? 'bg-gray-800' : 'bg-white';
  const textColor = darkMode ? 'text-gray-100' : 'text-gray-900';
  const mutedTextColor = darkMode ? 'text-gray-400' : 'text-gray-600';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  const urgentBg = darkMode ? 'bg-red-900/20' : 'bg-red-50';
  const urgentBorder = darkMode ? 'border-red-700' : 'border-red-300';
  const urgentText = darkMode ? 'text-red-400' : 'text-red-700';
  
  // Map icons to categories
  const categoryIcons = {
    preparation: <DocumentTextIcon className="w-5 h-5" />,
    documentation: <DocumentDuplicateIcon className="w-5 h-5" />,
    communication: <ChatBubbleLeftRightIcon className="w-5 h-5" />
  };
  
  // Function to determine if an item is auto-completed
  const isAutoCompleted = (item: ChecklistItem): boolean => {
    if (!item.auto_check) return false;
    
    switch (item.auto_check) {
      case 'has_completed_prep':
        return hasCompletedPrep;
      case 'evidence_coverage':
        return evidenceCoverage >= (item.threshold || 80);
      default:
        return false;
    }
  };
  
  // Process checklist items with completion status and icons
  const checklistItems = dynamicChecklist.map(item => ({
    ...item,
    icon: categoryIcons[item.category] || <CheckCircleIcon className="w-5 h-5" />,
    completed: isAutoCompleted(item)
  }));
  
  // Load saved checklist state
  useEffect(() => {
    const loadChecklistState = async () => {
      if (!user) return;
      
      try {
        const { data, error } = await supabase
          .from('review_prep_checklist')
          .select('checked_items')
          .eq('focus_id', focusId)
          .eq('user_id', user.id)
          .maybeSingle();
          
        if (error) {
          // Log but don't throw - table might not exist or no data yet
          devLog.warn('Error loading checklist state:', error);
        } else if (data && data.checked_items) {
          setCheckedItems(new Set(data.checked_items));
        }
      } catch (error) {
        devLog.error('Error loading checklist state:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadChecklistState();
  }, [focusId, user]);
  
  // Save checklist state
  const toggleItem = async (itemId: string) => {
    const newCheckedItems = new Set(checkedItems);
    if (newCheckedItems.has(itemId)) {
      newCheckedItems.delete(itemId);
    } else {
      newCheckedItems.add(itemId);
    }
    setCheckedItems(newCheckedItems);
    
    // Save to database
    if (user) {
      try {
        await supabase
          .from('review_prep_checklist')
          .upsert({
            focus_id: focusId,
            user_id: user.id,
            checked_items: Array.from(newCheckedItems),
            updated_at: new Date().toISOString()
          }, {
            onConflict: 'focus_id,user_id'
          });
      } catch (error) {
        devLog.error('Error saving checklist state:', error);
      }
    }
  };
  
  // Calculate completion percentage
  const completedCount = checklistItems.filter(item => 
    item.completed || checkedItems.has(item.id)
  ).length;
  const completionPercentage = Math.round((completedCount / checklistItems.length) * 100);
  
  // Group items by category
  const groupedItems = checklistItems.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {} as Record<string, typeof checklistItems>);
  
  const categoryLabels = {
    preparation: 'Preparation',
    documentation: 'Documentation Review',
    communication: 'Communication'
  };
  
  const categoryEmojis = {
    preparation: '📝',
    documentation: '📚',
    communication: '💬'
  };
  
  // Don't show if more than 7 days until review
  if (daysUntilReview > 7) {
    return null;
  }
  
  // Show loading state
  if (checklistLoading) {
    return (
      <div className={`${bgColor} rounded-lg border ${borderColor} p-6`}>
        <div className="flex items-center gap-3">
          <div className="animate-pulse">
            <div className={`w-12 h-12 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
          </div>
          <div className="flex-1 animate-pulse">
            <div className={`h-5 w-32 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} mb-2`} />
            <div className={`h-4 w-48 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
          </div>
        </div>
      </div>
    );
  }
  
  // Show error state if no checklist items
  if (checklistItems.length === 0) {
    return (
      <div className={`${bgColor} rounded-lg border ${borderColor} p-6`}>
        <div className="flex items-center gap-3">
          <ExclamationCircleIcon className={`w-6 h-6 ${mutedTextColor}`} />
          <p className={mutedTextColor}>Unable to load review checklist</p>
        </div>
      </div>
    );
  }
  
  const isUrgent = daysUntilReview <= 2;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`${isUrgent ? urgentBg : bgColor} rounded-lg border ${isUrgent ? urgentBorder : borderColor} overflow-hidden`}
    >
      {/* Header */}
      <div 
        className={`px-6 py-4 cursor-pointer ${isUrgent ? '' : `border-b ${borderColor}`}`}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${isUrgent ? 'bg-red-500/20' : darkMode ? 'bg-blue-900/20' : 'bg-blue-100'}`}>
              <CheckCircleIcon className={`w-5 h-5 ${isUrgent ? urgentText : 'text-blue-500'}`} />
            </div>
            <div>
              <h3 className={`font-semibold ${textColor}`}>
                Final Week Checklist
                {isUrgent && (
                  <span className={`ml-2 text-sm ${urgentText}`}>
                    • {daysUntilReview} day{daysUntilReview !== 1 ? 's' : ''} left!
                  </span>
                )}
              </h3>
              <p className={`text-sm ${mutedTextColor}`}>
                {completedCount} of {checklistItems.length} items completed ({completionPercentage}%)
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Progress Ring */}
            <div className="relative w-12 h-12">
              <svg className="w-12 h-12 transform -rotate-90">
                <circle
                  cx="24"
                  cy="24"
                  r="20"
                  stroke={darkMode ? '#374151' : '#E5E7EB'}
                  strokeWidth="4"
                  fill="none"
                />
                <circle
                  cx="24"
                  cy="24"
                  r="20"
                  stroke={isUrgent ? '#EF4444' : '#3B82F6'}
                  strokeWidth="4"
                  fill="none"
                  strokeDasharray={`${2 * Math.PI * 20}`}
                  strokeDashoffset={`${2 * Math.PI * 20 * (1 - completionPercentage / 100)}`}
                  className="transition-all duration-500"
                />
              </svg>
              <div className={`absolute inset-0 flex items-center justify-center text-xs font-semibold ${textColor}`}>
                {completionPercentage}%
              </div>
            </div>
            
            {/* Expand/Collapse Icon */}
            {isExpanded ? (
              <ChevronUpIcon className={`w-5 h-5 ${mutedTextColor}`} />
            ) : (
              <ChevronDownIcon className={`w-5 h-5 ${mutedTextColor}`} />
            )}
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className={`mt-3 h-2 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} overflow-hidden`}>
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${completionPercentage}%` }}
            transition={{ duration: 0.5 }}
            className={`h-full ${isUrgent ? 'bg-red-500' : 'bg-blue-500'}`}
          />
        </div>
      </div>
      
      {/* Checklist Items */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="px-6 py-4 space-y-6">
              {Object.entries(groupedItems).map(([category, items]) => (
                <div key={category} className="space-y-3">
                  <h4 className={`text-sm font-medium ${mutedTextColor} flex items-center gap-2`}>
                    <span>{categoryEmojis[category as keyof typeof categoryEmojis]}</span>
                    {categoryLabels[category as keyof typeof categoryLabels]}
                  </h4>
                  
                  <div className="space-y-2">
                    {items.map((item) => {
                      const isChecked = item.completed || checkedItems.has(item.id);
                      
                      return (
                        <motion.label
                          key={item.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          className={`
                            flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors
                            ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}
                            ${isChecked ? 'opacity-75' : ''}
                          `}
                        >
                          <div className="flex items-center mt-0.5">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => !item.completed && toggleItem(item.id)}
                              disabled={item.completed}
                              className={`
                                sr-only
                              `}
                            />
                            {isChecked ? (
                              <CheckCircleIconSolid className="w-5 h-5 text-green-500" />
                            ) : (
                              <div className={`
                                w-5 h-5 rounded border-2 
                                ${darkMode ? 'border-gray-600' : 'border-gray-300'}
                              `} />
                            )}
                          </div>
                          
                          <div className="flex-1">
                            <p className={`text-sm font-medium ${isChecked ? mutedTextColor : textColor}`}>
                              {item.label}
                              {item.completed && (
                                <span className="ml-2 text-xs text-green-500">
                                  (Auto-completed)
                                </span>
                              )}
                            </p>
                            <p className={`text-xs ${mutedTextColor} mt-0.5`}>
                              {item.description}
                            </p>
                          </div>
                          
                          <div className={`${isChecked ? 'opacity-50' : ''}`}>
                            {item.icon}
                          </div>
                        </motion.label>
                      );
                    })}
                  </div>
                </div>
              ))}
              
              {/* Motivational Message */}
              {completionPercentage === 100 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className={`
                    mt-4 p-4 rounded-lg text-center
                    ${darkMode ? 'bg-green-900/20 border-green-700' : 'bg-green-50 border-green-200'}
                    border
                  `}
                >
                  <p className={`text-sm font-medium ${darkMode ? 'text-green-300' : 'text-green-700'}`}>
                    🎉 You're fully prepared for your review! Good luck!
                  </p>
                </motion.div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default FinalWeekChecklist;